import { useMediaQuery, useTheme } from '@mui/material';
import type {
  IStylesOverrides,
  IResponsivePaddingOverride,
} from '../common-types';

export const useContentWrapperPadding = ({
  styleOverrides,
  defaultPadding,
}: {
  styleOverrides: IStylesOverrides;
  defaultPadding: number;
}) => {
  const theme = useTheme();
  const largeScreenSize = useMediaQuery(theme.breakpoints.up('lg'));
  const mediumScreenSize = useMediaQuery(theme.breakpoints.up('md'));
  const smallScreenSize = useMediaQuery(theme.breakpoints.up('sm'));

  const getResponsivePaddingValues = (paddingValue: number) => {
    const paddingValueOrDefault = paddingValue ?? defaultPadding;

    const effectivePaddingValue =
      paddingValueOrDefault === 0 ? 0.1 : paddingValueOrDefault;

    if (effectivePaddingValue <= defaultPadding) {
      return effectivePaddingValue;
    }

    return {
      xs: effectivePaddingValue * 0.5,
      sm: effectivePaddingValue * 0.6,
      md: effectivePaddingValue * 0.8,
      lg: effectivePaddingValue,
    };
  };

  const getPaddingOverrideValueForPosition = (position: 'top' | 'bottom') => {
    const paddingConfig =
      position === 'bottom'
        ? styleOverrides?.paddingBottom
        : styleOverrides?.paddingTop;

    return paddingConfig;
  };

  const getPaddingForScreenSize = (
    responsivePaddingOverride: IResponsivePaddingOverride
  ) => {
    if (typeof responsivePaddingOverride !== 'object') {
      return responsivePaddingOverride;
    }

    const { xs, sm, md, lg } = responsivePaddingOverride;
    if (largeScreenSize) {
      return lg;
    }
    if (mediumScreenSize) {
      return md;
    }
    if (smallScreenSize) {
      return sm;
    }
    return xs;
  };

  const containerPadding = (position: 'top' | 'bottom') => {
    const paddingOverrideValue = getPaddingOverrideValueForPosition(position);

    if (paddingOverrideValue === undefined || paddingOverrideValue === null) {
      return theme.spacing(defaultPadding);
    }

    const responsivePaddingValues =
      getResponsivePaddingValues(paddingOverrideValue);

    const paddingForScreenSize = getPaddingForScreenSize(
      responsivePaddingValues
    );

    return theme.spacing(paddingForScreenSize);
  };

  return containerPadding;
};
